/**
 * BabyGrowthPage
 *
 * Main page for baby growth tracking with WHO percentile charts.
 * Shows weight, height, and head circumference tabs.
 */

import { useState } from 'react';
import { Loader2, TrendingUp, Plus, Lock, Baby, Scale, Ruler, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { isValidUUID } from '@/lib/utils';
import { useBabyGrowth } from '../hooks/useBabyGrowth';
import { GrowthChart } from '../components/GrowthChart';
import { GrowthMeasurementModal } from '../components/GrowthMeasurementModal';
import { GrowthMeasurementList } from '../components/GrowthMeasurementList';
import {
  MeasurementType,
  getMeasurementInfo,
  calculatePercentile,
  getPercentileLabel,
} from '../data/whoGrowthStandards';
import { BabyGrowthEntry, CreateBabyGrowthEntry, UpdateBabyGrowthEntry } from '@/services/babyGrowthService';

export function BabyGrowthPage() {
  const { t } = useTranslation('babyGrowth');
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const isBabyGrowthEnabled = useFeatureEnabled('baby_growth_enabled');

  // Tabs for different measurement types (inside component for i18n)
  const TAB_ICONS: Record<MeasurementType, React.ComponentType<{ className?: string }>> = {
    weight: Scale,
    height: Ruler,
    head: Circle,
  };

  const TABS: { type: MeasurementType; label: string }[] = [
    { type: 'weight', label: t('tabs.weight') },
    { type: 'height', label: t('tabs.height') },
    { type: 'head', label: t('tabs.head') },
  ];

  // Localized override for getPercentileLabel
  const getLocalizedPercentileLabel = (percentile: number) => {
    const info = getPercentileLabel(percentile);
    const descriptionMap: Record<string, string> = {
      'Deutlich unter dem Durchschnitt': t('percentile.wellBelow'),
      'Unter dem Durchschnitt': t('percentile.below'),
      'Im Normalbereich': t('percentile.normal'),
      'Über dem Durchschnitt': t('percentile.above'),
      'Deutlich über dem Durchschnitt': t('percentile.wellAbove'),
    };
    return { ...info, description: descriptionMap[info.description] || info.description };
  };

  // Localized override for formatAge
  const localizedFormatAge = (ageInDays: number): string => {
    if (ageInDays === 0) return t('age.birth');
    if (ageInDays < 7) return `${ageInDays} ${ageInDays === 1 ? t('age.day') : t('age.days')}`;
    if (ageInDays < 30) {
      const weeks = Math.floor(ageInDays / 7);
      return `W${weeks}`;
    }
    const months = Math.floor(ageInDays / 30.44);
    return `M${months}`;
  };

  // State
  const [activeTab, setActiveTab] = useState<MeasurementType>('weight');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BabyGrowthEntry | undefined>();

  // Validate pregnancy ID
  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Check if user is in baby mode with birth completion
  const wasBirth = lastCompletedPregnancy?.completion_type === 'birth';
  const isBabyMode = appMode === 'baby' && wasBirth;

  // Get baby growth data
  const {
    entries,
    latestEntry,
    entryCount,
    babies,
    selectedBabyIndex,
    birthDate,
    isLoading,
    isCreating,
    error,
    maxEntries,
    isUnlimited,
    canAddEntry,
    setSelectedBabyIndex,
    addEntry,
    updateEntry,
    deleteEntry,
    getBabyGender,
  } = useBabyGrowth();

  // Get current baby gender for percentile calculations
  const gender = getBabyGender();

  // Handle save
  const handleSave = async (
    data: Omit<CreateBabyGrowthEntry, 'pregnancy_id' | 'baby_index'> | UpdateBabyGrowthEntry,
    id?: string
  ) => {
    if (id) {
      await updateEntry(id, data);
    } else {
      await addEntry(data as Omit<CreateBabyGrowthEntry, 'pregnancy_id' | 'baby_index'>);
    }
    setIsModalOpen(false);
    setEditingEntry(undefined);
  };

  // Handle edit
  const handleEdit = (entry: BabyGrowthEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  // Handle new entry
  const handleNewEntry = () => {
    setEditingEntry(undefined);
    setIsModalOpen(true);
  };

  // Show disabled state if feature is not enabled
  if (isBabyGrowthEnabled === false) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Show message if not in baby mode or no birth completion
  if (!isBabyMode || !pregnancyId) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <TrendingUp className="w-8 h-8 text-white/40" />
        </div>
        <h2 className="text-white font-medium text-lg mb-2">{t('pageTitle')}</h2>
        <p className="text-white/60 text-sm max-w-xs mx-auto">
          {t('notAvailable')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Get latest value for current tab
  const getLatestValue = (): { value: number; ageInDays: number } | null => {
    if (!latestEntry || !birthDate) return null;

    const birth = new Date(birthDate);
    const measured = new Date(latestEntry.measured_at);
    const ageInDays = Math.floor((measured.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));

    let value: number | null = null;
    if (activeTab === 'weight') value = latestEntry.weight_grams;
    else if (activeTab === 'height') value = latestEntry.height_cm ? Number(latestEntry.height_cm) : null;
    else if (activeTab === 'head') value = latestEntry.head_circumference_cm ? Number(latestEntry.head_circumference_cm) : null;

    if (value === null) return null;
    return { value, ageInDays };
  };

  const latestData = getLatestValue();
  const measurementInfo = getMeasurementInfo(activeTab);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-white text-xl font-semibold">{t('pageTitle')}</h1>
      </div>

      {/* Baby Selector (for multiples) */}
      {babies.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {babies.map((baby, index) => (
            <button
              key={index}
              onClick={() => setSelectedBabyIndex(index)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                selectedBabyIndex === index
                  ? 'bg-pp-orange text-white'
                  : 'glass-effect text-white/70 hover:text-white'
              }`}
            >
              <Baby className="w-4 h-4" />
              {baby.name}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = TAB_ICONS[tab.type];
          const isActive = activeTab === tab.type;

          return (
            <button
              key={tab.type}
              onClick={() => setActiveTab(tab.type)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all whitespace-nowrap border ${
                isActive
                  ? 'bg-pp-orange border-pp-orange text-white'
                  : 'bg-[rgba(31,41,55,0.35)] backdrop-blur-[5px] border-white/25 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon className="w-5 h-5" />
              {isActive && (
                <span className="text-sm font-medium">
                  {tab.label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <section className="mb-4">
        <GrowthChart
          entries={entries}
          type={activeTab}
          gender={gender}
          birthDate={birthDate || ''}
          maxAgeInDays={Math.max(60, entries.length > 0 ? Math.ceil(
            (new Date(entries[0].measured_at).getTime() - new Date(birthDate || '').getTime()) / (1000 * 60 * 60 * 24) * 1.2
          ) : 60)}
        />
      </section>

      {/* Latest Value Card */}
      {latestData && (
        <section className="mb-4">
          <div className="glass-effect rounded-[15px] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">{t('current', { age: localizedFormatAge(latestData.ageInDays) })}</p>
                <p className="text-white text-2xl font-bold mt-1">
                  {activeTab === 'weight'
                    ? latestData.value >= 1000
                      ? `${(latestData.value / 1000).toFixed(3).replace(/\.?0+$/, '')} kg`
                      : `${latestData.value} g`
                    : `${latestData.value.toFixed(1)} ${measurementInfo.unit}`
                  }
                </p>
              </div>
              <div className="text-right">
                {(() => {
                  const percentile = calculatePercentile(latestData.value, latestData.ageInDays, activeTab, gender);
                  const info = getLocalizedPercentileLabel(percentile);
                  return (
                    <>
                      <p className={`text-2xl font-bold ${info.color}`}>P{percentile}</p>
                      <p className="text-white/40 text-xs mt-1">{info.description}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Add Button */}
      <section className="mb-4">
        {canAddEntry ? (
          <button
            onClick={handleNewEntry}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-[15px] bg-pp-orange text-white font-semibold hover:bg-pp-orange/90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('newMeasurement')}
          </button>
        ) : (
          <div className="glass-effect rounded-[15px] p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-pp-orange mb-2">
              <Lock className="w-5 h-5" />
              <span className="font-medium">{t('limitReached')}</span>
            </div>
            <p className="text-white/60 text-sm mb-3">
              {t('limitDescription', { max: maxEntries })}
            </p>
            <Link
              to="/subscription"
              className="inline-block px-6 py-2 rounded-full bg-pp-orange text-white text-sm font-medium hover:bg-pp-orange/90 transition-colors"
            >
              {t('unlockPremium')}
            </Link>
          </div>
        )}
      </section>

      {/* Entry count for free users */}
      {!isUnlimited && (
        <p className="text-white/40 text-xs text-center mb-4">
          {t('measurementCount', { count: entryCount, max: maxEntries })}
        </p>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 rounded-[10px] bg-red-500/20 text-red-400 text-sm text-center">
          {error}
        </div>
      )}

      {/* Measurement List */}
      <section>
        <h2 className="text-white/70 text-sm font-medium mb-3">{t('allMeasurements')}</h2>
        <GrowthMeasurementList
          entries={entries}
          gender={gender}
          birthDate={birthDate || ''}
          onEdit={handleEdit}
          onDelete={deleteEntry}
        />
      </section>

      {/* Modal */}
      <GrowthMeasurementModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingEntry(undefined);
        }}
        onSave={handleSave}
        existingEntry={editingEntry}
        isLoading={isCreating}
      />
    </>
  );
}
